import React, { useState, useMemo, useEffect, useRef } from 'react';
import { C, tooltipStyle } from '../utils/theme.js';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import Card from './Card.jsx';
import StatBox from './StatBox.jsx';
import { t } from '../utils/i18n.js';
import { DXCC_LIST } from '../data/dxcc.js';
import { ENTITY_COORDS } from '../data/entityCoords.js';
import { lookupDXCC } from '../utils/dxccLookup.js';
import { addDarkBasemap } from '../utils/mapTiles.js';
import DXEffectiveness from './DXEffectiveness.jsx';

var API = window.location.hostname === "localhost" ? "http://localhost:3001" : "";

var CONT_NAMES = { AF: "Africa", AN: "Antarctica", AS: "Asia", EU: "Europe", NA: "N. America", OC: "Oceania", SA: "S. America" };
var CONT_LIST = ["EU", "NA", "SA", "AS", "AF", "OC"];
// PSK Reporter / QRZ default band palette (recognizable band-by-colour).
var BAND_COLORS = {
  "160m": "#7cfc00", "80m": "#e550e5", "60m": "#00008b", "40m": "#5959ff",
  "30m": "#62d962", "20m": "#f2c40c", "17m": "#f2f261", "15m": "#cca166",
  "12m": "#b22222", "10m": "#ff69b4", "6m": "#ff0000", "2m": "#ff1493", "70cm": "#999900"
};

// Build a lookup: entity name -> { cont, cq } from the canonical list
var ENTITY_INFO = {};
for (var ei = 0; ei < DXCC_LIST.length; ei++) {
  ENTITY_INFO[DXCC_LIST[ei].entity] = { cont: DXCC_LIST[ei].cont, cq: DXCC_LIST[ei].cq };
}

function tr(lang, pt, en) { return lang === "pt" ? pt : en; }

// ── CQ WPX prefix extractor (handles common portable cases) ───
var FIVE_BANDS = ["80m", "40m", "20m", "15m", "10m"];
function wpxPrefix(raw) {
  if (!raw) return null;
  var call = String(raw).toUpperCase().replace(/[^A-Z0-9/]/g, "");
  if (!call) return null;
  var segs = call.split("/").filter(Boolean);
  var SUFFIX = { P: 1, M: 1, MM: 1, AM: 1, QRP: 1, A: 1, B: 1, R: 1 };
  if (segs.length > 1) {
    var main = null, locPfx = null, addDigit = null;
    for (var i = 0; i < segs.length; i++) {
      var s = segs[i];
      if (SUFFIX[s]) continue;
      if (/^\d$/.test(s)) { addDigit = s; continue; } // e.g. PY2ABC/7
      if (s.length >= 4 && /[A-Z]/.test(s) && /\d/.test(s)) {
        if (!main || s.length > main.length) main = s; // the home callsign
      } else if (!locPfx || s.length < locPfx.length) {
        locPfx = s; // location designator e.g. DL, VP2E, 4X
      }
    }
    if (locPfx) return /\d/.test(locPfx) ? locPfx : locPfx + "0";
    if (addDigit && main) { var mm = main.match(/^(\d?[A-Z]+)\d/); if (mm) return mm[1] + addDigit; }
    call = main || segs[0];
  }
  var m = call.match(/^(\d?[A-Z]+\d+)/);
  if (m) return m[1];
  var letters = call.match(/^\d?[A-Z]+/);
  return letters ? letters[0] + "0" : null;
}

// ── Animated count-up number ──────────────────────────────────
function CountUp(props) {
  var target = props.value || 0;
  var valSt = useState(0); var val = valSt[0]; var setVal = valSt[1];
  useEffect(function() {
    var start = null; var from = 0; var dur = 900;
    var raf;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return function() { if (raf) cancelAnimationFrame(raf); };
  }, [target]);
  return <span>{val}</span>;
}

// ── Animated circular progress ring (SVG donut) ───────────────
function Ring(props) {
  var size = props.size || 132;
  var stroke = props.stroke || 9;
  var r = (size - stroke) / 2;
  var circ = 2 * Math.PI * r;
  var pct = props.total > 0 ? Math.min(1, props.value / props.total) : 0;
  var dashSt = useState(circ); var dash = dashSt[0]; var setDash = dashSt[1];
  useEffect(function() {
    var id = setTimeout(function() { setDash(circ * (1 - pct)); }, 80);
    return function() { clearTimeout(id); };
  }, [pct, circ]);
  var gid = "rg_" + (props.label || "").replace(/[^a-z]/gi, "");
  var c2 = props.color2 || props.color;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={props.color} />
            <stop offset="100%" stopColor={c2} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.s3} strokeWidth={stroke} strokeOpacity={0.5} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={"url(#" + gid + ")"} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash}
          style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(.22,1,.36,1)", filter: "drop-shadow(0 0 6px " + props.color + "99)" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 30, fontWeight: 900, color: props.color, fontFamily: "'Courier New',monospace", lineHeight: 1, letterSpacing: -0.5 }}>
          {props.centerRaw != null ? props.centerRaw : <CountUp value={props.value} />}
        </div>
        <div style={{ fontSize: 10, color: C.t3, marginTop: 3 }}>{props.centerSub}</div>
      </div>
    </div>
  );
}

// ── Award tile wrapping a Ring (discrete glass card + icon chip) ──
function AwardTile(props) {
  var col = props.color;
  return (
    <div style={{
      flex: "1 1 200px", minWidth: 200, maxWidth: 300,
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "20px 16px 18px",
      background: C.s2 + "66",
      border: "1px solid " + C.bd,
      borderRadius: 16,
      backdropFilter: "blur(4px)"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 15, borderRadius: 8, background: col + "1c", border: "1px solid " + col + "3a" }}>{props.icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: C.t1, fontFamily: "'Courier New',monospace", letterSpacing: 0.2 }}>{props.title}</span>
      </div>
      <Ring value={props.value} total={props.total} color={props.color} color2={props.color2} centerRaw={props.centerRaw} centerSub={props.centerSub} />
      <div style={{ marginTop: 14, fontSize: 10.5, color: C.t2, textAlign: "center", lineHeight: 1.4, minHeight: 28, display: "flex", alignItems: "center" }}>{props.sub}</div>
    </div>
  );
}

// ── World achievement map (Leaflet, plotted by status) ────────
function AwardMap(props) {
  var points = props.points; // [{ entity, status }]
  var mapRef = useRef(null);
  var mapObjRef = useRef(null);
  var readySt = useState(!!window.L); var ready = readySt[0]; var setReady = readySt[1];

  useEffect(function() {
    if (window.L) { setReady(true); return; }
    if (document.getElementById("lf-css")) { var iv = setInterval(function() { if (window.L) { setReady(true); clearInterval(iv); } }, 200); return function() { clearInterval(iv); }; }
    var css = document.createElement("link"); css.id = "lf-css"; css.rel = "stylesheet";
    css.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(css);
    var js = document.createElement("script");
    js.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    js.onload = function() { setReady(true); };
    document.head.appendChild(js);
  }, []);

  useEffect(function() {
    if (!ready || !mapRef.current || !window.L) return;
    if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }
    var map = window.L.map(mapRef.current, { preferCanvas: true, zoomControl: true, attributionControl: false }).setView([20, 0], 2);
    mapObjRef.current = map;
    addDarkBasemap(map, { maxZoom: 8, minZoom: 1 });
    var colors = { confirmed: C.a2, worked: "#eab308", needed: C.a5 };
    var order = { needed: 0, worked: 1, confirmed: 2 }; // draw confirmed on top
    var sorted = points.slice().sort(function(a, b) { return order[a.status] - order[b.status]; });
    for (var i = 0; i < sorted.length; i++) {
      var p = sorted[i];
      var co = ENTITY_COORDS[p.entity];
      if (!co) continue;
      var col = colors[p.status] || C.t3;
      window.L.circleMarker([co.lat, co.lon], {
        radius: p.status === "needed" ? 3 : 4,
        color: col, fillColor: col, fillOpacity: p.status === "needed" ? 0.45 : 0.85, weight: 0
      }).addTo(map).bindPopup('<div style="font-family:monospace;font-size:12px"><b style="color:' + col + '">' + p.entity + '</b><br/>' + (p.statusLabel || p.status) + '</div>');
    }
    setTimeout(function() { map.invalidateSize(); }, 250);
    return function() { if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; } };
  }, [ready, points]);

  return <div ref={mapRef} style={{ height: 360, width: "100%", borderRadius: 12, overflow: "hidden", background: C.s2 }} />;
}

// Progress bar component
function Bar(props) {
  var pct = props.total > 0 ? Math.min(100, (props.value / props.total) * 100) : 0;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.t3, marginBottom: 3 }}>
        <span>{props.label}</span>
        <span style={{ color: props.color, fontFamily: "monospace", fontWeight: 700 }}>{props.value} / {props.total} ({pct.toFixed(1)}%)</span>
      </div>
      <div style={{ height: 10, background: C.s3, borderRadius: 6, overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: "linear-gradient(90deg," + props.color + "," + (props.color2 || props.color) + ")", borderRadius: 6, transition: "width .4s" }}></div>
      </div>
    </div>
  );
}

export default function DXAwardsView(props) {
  var lang = props.lang;
  var qsos = props.qsos || [];
  var stats = props.stats;
  var callsign = (props.callsign || "").toUpperCase();

  var tabSt = useState("marathon"); var tab = tabSt[0]; var setTab = tabSt[1];
  var nowYear = new Date().getUTCFullYear();
  var yearSt = useState(nowYear); var year = yearSt[0]; var setYear = yearSt[1];
  var hrSearchSt = useState(""); var hrSearch = hrSearchSt[0]; var setHrSearch = hrSearchSt[1];
  var hrContSt = useState(""); var hrCont = hrContSt[0]; var setHrCont = hrContSt[1];
  var hrStatusSt = useState(""); var hrStatus = hrStatusSt[0]; var setHrStatus = hrStatusSt[1];
  // DXCC Challenge (band/mode matrix) state
  var matrixTabSt = useState("band"); var matrixTab = matrixTabSt[0]; var setMatrixTab = matrixTabSt[1];
  var chSearchSt = useState(""); var chSearch = chSearchSt[0]; var setChSearch = chSearchSt[1];
  var chContSt = useState(""); var chCont = chContSt[0]; var setChCont = chContSt[1];
  var chStatusSt = useState(""); var chStatus = chStatusSt[0]; var setChStatus = chStatusSt[1];

  var TOTAL_ENTITIES = DXCC_LIST.length;
  var HR_THRESHOLD = TOTAL_ENTITIES - 9; // ARRL DXCC Honor Roll = top 10 (total minus 9)

  // ── DX Marathon: aggregate per calendar year ──────────────────
  var marathon = useMemo(function() {
    var perYear = {}; // year -> { entities:Set, zones:Set, qsos:int }
    for (var i = 0; i < qsos.length; i++) {
      var q = qsos[i];
      if (!q.date || q.date.length < 4) continue;
      var y = q.date.substring(0, 4);
      if (!perYear[y]) perYear[y] = { entities: new Set(), zones: new Set(), qsos: 0 };
      perYear[y].qsos++;
      if (q.entity && q.entity !== "Unknown") perYear[y].entities.add(q.entity);
      var cq = parseInt(q.cq, 10);
      if (cq > 0 && cq <= 40) perYear[y].zones.add(cq);
    }
    // History: array of { year, entities, zones, score }
    var history = Object.keys(perYear).sort().reverse().map(function(y) {
      return { year: y, entities: perYear[y].entities.size, zones: perYear[y].zones.size, score: perYear[y].entities.size + perYear[y].zones.size, qsos: perYear[y].qsos };
    });
    return { perYear: perYear, history: history };
  }, [qsos]);

  var yStr = String(year);
  var yData = marathon.perYear[yStr] || { entities: new Set(), zones: new Set(), qsos: 0 };
  var yEntities = yData.entities.size;
  var yZones = yData.zones.size;
  var yScore = yEntities + yZones;
  var bestScore = marathon.history.reduce(function(m, h) { return h.score > m ? h.score : m; }, 0);

  // Per-year detail: list of entities (first worked date/band/mode in the year) + worked zones
  var yearDetail = useMemo(function() {
    var entFirst = {}; // entity -> { date, band, mode }
    var zoneSet = {};
    for (var i = 0; i < qsos.length; i++) {
      var q = qsos[i];
      if (!q.date || q.date.substring(0, 4) !== yStr) continue;
      if (q.entity && q.entity !== "Unknown") {
        if (!entFirst[q.entity] || q.date < entFirst[q.entity].date) {
          entFirst[q.entity] = { date: q.date, band: q.band || "?", mode: q.mode || "?", cont: (ENTITY_INFO[q.entity] || {}).cont || "??" };
        }
      }
      var cq = parseInt(q.cq, 10);
      if (cq > 0 && cq <= 40) zoneSet[cq] = true;
    }
    var entList = Object.keys(entFirst).map(function(e) { return { entity: e, date: entFirst[e].date, band: entFirst[e].band, mode: entFirst[e].mode, cont: entFirst[e].cont }; });
    entList.sort(function(a, b) { return b.date.localeCompare(a.date); });
    return { entList: entList, zoneSet: zoneSet };
  }, [qsos, yStr]);

  function fmtDate(d) { return d && d.length === 8 ? d.slice(0, 4) + "-" + d.slice(4, 6) + "-" + d.slice(6, 8) : d; }

  // ── DXCC Honor Roll: all-time confirmed-based ─────────────────
  var dd = (stats && stats.dxccData) || {};
  // DXCC confirmado = entidades confirmadas no LoTW (número autoritativo, igual ao
  // dashboard e ao LoTW). Prefere o DXCC oficial por QSO (lotw_dxcc); QRZ não conta.
  var confirmedCount = (stats && stats.dxccLotwConfirmedCount) || 0;
  var workedCount = (stats && stats.dxccWorkedCount) || 0;
  var neededForHR = Math.max(0, HR_THRESHOLD - confirmedCount);
  var neededForTop = Math.max(0, TOTAL_ENTITIES - confirmedCount);
  var inHonorRoll = confirmedCount >= HR_THRESHOLD;
  var isNumberOne = confirmedCount >= TOTAL_ENTITIES;

  // Atencao ao renderizar: o NOME da entidade se repete na tabela DXCC
  // (Antartica CE9/RI1AN, Saba PJ5/PJ6, Russia R/UA), entao a key das listas
  // tem de ser o prefixo, que e unico. Com key repetida o React descarta linhas.
  // Build entity rows w/ status, plus per-continent confirmed summary
  var hrRows = useMemo(function() {
    return DXCC_LIST.map(function(d) {
      var info = dd[d.entity];
      return {
        entity: d.entity, px: d.px, cont: d.cont, cq: d.cq,
        worked: info ? true : false,
        confirmed: info ? !!info.confirmed : false
      };
    });
  }, [dd]);

  var contSummary = useMemo(function() {
    var s = {};
    for (var i = 0; i < hrRows.length; i++) {
      var c = hrRows[i].cont;
      if (!s[c]) s[c] = { total: 0, worked: 0, confirmed: 0 };
      s[c].total++;
      if (hrRows[i].worked) s[c].worked++;
      if (hrRows[i].confirmed) s[c].confirmed++;
    }
    return s;
  }, [hrRows]);

  // Worked-but-not-confirmed = QSL pending (actionable for Honor Roll)
  var pendingConfirm = hrRows.filter(function(r) { return r.worked && !r.confirmed; });

  // ── DXCC Challenge: band/mode matrix ──────────────────────────
  var BAND_ORDER = ["160m", "80m", "40m", "30m", "20m", "17m", "15m", "12m", "10m", "6m"];
  var MODE_ORDER = ["SSB", "CW", "FT8", "FT4", "RTTY", "AM", "FM"];
  var activeModes = useMemo(function() {
    var used = {};
    for (var ek in dd) { for (var mk in dd[ek].modes) used[mk] = 1; }
    return MODE_ORDER.filter(function(m) { return used[m]; });
  }, [dd]);
  var chRows = useMemo(function() {
    return DXCC_LIST.map(function(d) {
      var info = dd[d.entity];
      return { entity: d.entity, px: d.px, cont: d.cont, cq: d.cq, worked: info ? true : false, confirmed: info ? !!info.confirmed : false, bands: info ? info.bands : {}, modes: info ? info.modes : {} };
    });
  }, [dd]);
  var chSf = chSearch.toUpperCase();
  var chFiltered = chRows.filter(function(r) {
    if (chCont && r.cont !== chCont) return false;
    if (chStatus === "worked" && !r.worked) return false;
    if (chStatus === "confirmed" && !r.confirmed) return false;
    if (chStatus === "needed" && r.worked) return false;
    if (chSf && r.entity.toUpperCase().indexOf(chSf) < 0) return false;
    return true;
  });
  var matrixBtn = function(id, label) {
    var active = matrixTab === id;
    return <button key={id} onClick={function() { setMatrixTab(id); }} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: active ? C.a1 : C.s3, color: active ? "#fff" : C.t3, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>;
  };

  // Filtered table
  var sf = hrSearch.toUpperCase();
  var filtered = hrRows.filter(function(r) {
    if (hrCont && r.cont !== hrCont) return false;
    if (hrStatus === "confirmed" && !r.confirmed) return false;
    if (hrStatus === "worked" && !(r.worked && !r.confirmed)) return false;
    if (hrStatus === "needed" && r.worked) return false;
    if (sf && r.entity.toUpperCase().indexOf(sf) < 0 && r.px.toUpperCase().indexOf(sf) < 0) return false;
    return true;
  });

  // ── Cumulative DXCC-over-time curve + Honor Roll projection ───
  var cumulative = useMemo(function() {
    var firstWorked = {}; // entity -> earliest date
    for (var i = 0; i < qsos.length; i++) {
      var q = qsos[i];
      if (!q.entity || q.entity === "Unknown" || !q.date || q.date.length < 8) continue;
      if (!firstWorked[q.entity] || q.date < firstWorked[q.entity]) firstWorked[q.entity] = q.date;
    }
    var dates = Object.keys(firstWorked).map(function(e) { return firstWorked[e]; }).sort();
    var series = []; var seen = {};
    var byMonth = {};
    for (var j = 0; j < dates.length; j++) {
      var d = dates[j]; var ym = d.slice(0, 4) + "-" + d.slice(4, 6);
      byMonth[ym] = (byMonth[ym] || 0) + 1;
    }
    var months = Object.keys(byMonth).sort();
    var run = 0;
    for (var k = 0; k < months.length; k++) { run += byMonth[months[k]]; series.push({ month: months[k], dxcc: run }); }
    // Projection: entities/day over the last ~2 years of activity
    var proj = null;
    if (dates.length >= 5) {
      var firstD = dates[0], lastD = dates[dates.length - 1];
      function toTs(s) { return new Date(parseInt(s.slice(0, 4)), parseInt(s.slice(4, 6)) - 1, parseInt(s.slice(6, 8))).getTime(); }
      var spanDays = Math.max(1, (toTs(lastD) - toTs(firstD)) / 86400000);
      var ratePerDay = workedCount / spanDays; // worked entities/day
      if (ratePerDay > 0) {
        var remainingToHR = Math.max(0, HR_THRESHOLD - workedCount);
        var daysToHR = remainingToHR / ratePerDay;
        var eta = new Date(Date.now() + daysToHR * 86400000);
        proj = { ratePerYear: ratePerDay * 365, daysToHR: daysToHR, etaYear: eta.getUTCFullYear(), etaMonth: eta.getUTCMonth() + 1, remainingToHR: remainingToHR };
      }
    }
    return { series: series, proj: proj };
  }, [qsos, workedCount]);

  // ── 5BDXCC: 100 confirmed entities on each of 80/40/20/15/10m ──
  var fiveBand = useMemo(function() {
    var perBand = {};
    FIVE_BANDS.forEach(function(b) { perBand[b] = { worked: 0, confirmed: 0 }; });
    for (var ent in dd) {
      var bands = dd[ent].bands || {};
      FIVE_BANDS.forEach(function(b) {
        var cell = bands[b];
        if (cell && cell.w) perBand[b].worked++;
        if (cell && cell.c) perBand[b].confirmed++;
      });
    }
    var bandsComplete = FIVE_BANDS.filter(function(b) { return perBand[b].confirmed >= 100; }).length;
    return { perBand: perBand, bandsComplete: bandsComplete };
  }, [dd]);

  // ── CQ WPX: unique callsign prefixes (worked + confirmed) ─────
  var wpx = useMemo(function() {
    var worked = {}; var confirmed = {};
    for (var i = 0; i < qsos.length; i++) {
      var p = wpxPrefix(qsos[i].call);
      if (!p) continue;
      worked[p] = true;
      if (qsos[i].lotwQsl || qsos[i].qrzQsl || qsos[i].comment === "LoTW" || qsos[i].comment === "QRZ-C") confirmed[p] = true;
    }
    return { worked: Object.keys(worked).length, confirmed: Object.keys(confirmed).length };
  }, [qsos]);

  // ── Live DX-cluster spots ("Hunt now") ────────────────────────
  var spotsSt = useState([]); var spots = spotsSt[0]; var setSpots = spotsSt[1];
  var spotsLoadSt = useState(true); var spotsLoad = spotsLoadSt[0]; var setSpotsLoad = spotsLoadSt[1];
  var spotsErrSt = useState(false); var spotsErr = spotsErrSt[0]; var setSpotsErr = spotsErrSt[1];
  useEffect(function() {
    var alive = true;
    function load() {
      fetch(API + "/api/realtime/spots?limit=3000").then(function(r) { return r.json(); }).then(function(data) {
        if (!alive) return;
        setSpots(data.spots || []); setSpotsLoad(false); setSpotsErr(false);
      }).catch(function() { if (alive) { setSpotsLoad(false); setSpotsErr(true); } });
    }
    load();
    var iv = setInterval(load, 60000);
    return function() { alive = false; clearInterval(iv); };
  }, []);

  // ── Annual DX Marathon ranking (server-side, across all users) ─
  var rankSt = useState(null); var rank = rankSt[0]; var setRank = rankSt[1];
  var rankLoadSt = useState(false); var rankLoad = rankLoadSt[0]; var setRankLoad = rankLoadSt[1];
  useEffect(function() {
    if (tab !== "marathon") return;
    var alive = true; setRankLoad(true);
    fetch(API + "/api/rankings/dxmarathon?year=" + year).then(function(r) { return r.json(); }).then(function(d) {
      if (alive) { setRank(d && d.rows ? d : { rows: [], totalOperators: 0 }); setRankLoad(false); }
    }).catch(function() { if (alive) { setRank({ rows: [], totalOperators: 0 }); setRankLoad(false); } });
    return function() { alive = false; };
  }, [tab, year]);

  // Sets of what's already worked (all-time and this year)
  var workedEntitiesAll = useMemo(function() {
    var s = {}; for (var e in dd) s[e] = true; return s;
  }, [dd]);

  // Enrich live spots with entity + "why it matters" relative to active tab
  var huntSpots = useMemo(function() {
    if (!spots.length) return [];
    var out = []; var seenCall = {};
    var yearEnts = yearDetail ? null : null;
    for (var i = spots.length - 1; i >= 0 && out.length < 60; i--) {
      var s = spots[i];
      if (!s.sc || seenCall[s.sc + s.b]) continue;
      var info = lookupDXCC(s.sc);
      var ent = info ? info.entity : "Unknown";
      if (ent === "Unknown") continue;
      var cq = info ? info.cq : 0;
      var tags = [];
      if (tab === "marathon") {
        var needEnt = !yearDetail.entList.some(function(x) { return x.entity === ent; });
        var needZone = cq > 0 && cq <= 40 && !yearDetail.zoneSet[cq];
        if (needEnt) tags.push({ txt: tr(lang, "+1 entidade", "+1 entity"), color: C.a6 });
        if (needZone) tags.push({ txt: tr(lang, "+1 zona CQ" + (cq ? " (" + cq + ")" : ""), "+1 CQ zone" + (cq ? " (" + cq + ")" : "")), color: C.a4 });
        if (!tags.length) continue; // only show point-scoring spots
      } else {
        var hrInfo = dd[ent];
        if (!hrInfo) tags.push({ txt: tr(lang, "Novo DXCC", "New DXCC"), color: C.a5 });
        else if (!hrInfo.confirmed) tags.push({ txt: tr(lang, "Confirma p/ Honor Roll", "Confirm for Honor Roll"), color: "#eab308" });
        if (!tags.length) continue; // already confirmed — skip
      }
      seenCall[s.sc + s.b] = true;
      out.push({ sc: s.sc, b: s.b, m: s.m, f: s.f, snr: s.snr, ent: ent, cont: info ? info.cont : "??", cq: cq, tags: tags });
    }
    return out;
  }, [spots, tab, yearDetail, dd, lang]);

  // Map points for the active tab
  var mapPoints = useMemo(function() {
    if (tab === "marathon") {
      var workedThisYear = {};
      yearDetail.entList.forEach(function(e) { workedThisYear[e.entity] = true; });
      return DXCC_LIST.map(function(d) {
        return { entity: d.entity, status: workedThisYear[d.entity] ? "confirmed" : "needed", statusLabel: workedThisYear[d.entity] ? tr(lang, "Trabalhada em " + year, "Worked in " + year) : tr(lang, "Não trabalhada no ano", "Not worked this year") };
      });
    }
    return hrRows.map(function(r) {
      var st = r.confirmed ? "confirmed" : r.worked ? "worked" : "needed";
      var lbl = r.confirmed ? tr(lang, "Confirmada", "Confirmed") : r.worked ? tr(lang, "Pendente QSL", "QSL pending") : tr(lang, "Faltando", "Needed");
      return { entity: r.entity, status: st, statusLabel: lbl };
    });
  }, [tab, hrRows, yearDetail, year, lang]);

  // ── Shared styles ─────────────────────────────────────────────
  var tabBtn = function(id, label, icon) {
    var active = tab === id;
    return (
      <button key={id} onClick={function() { setTab(id); }} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid " + (active ? C.a1 : C.bd), background: active ? C.a1 + "20" : C.s2, color: active ? C.a1 : C.t2, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Courier New',monospace" }}>{icon} {label}</button>
    );
  };
  var hint = { fontSize: 11, color: C.t3, lineHeight: 1.6 };

  // ── Render helpers (shared between tabs) ──────────────────────
  function renderMap() {
    var legend = tab === "marathon"
      ? [[C.a2, tr(lang, "Trabalhada no ano", "Worked this year")], [C.a5, tr(lang, "Faltando no ano", "Missing this year")]]
      : [[C.a2, tr(lang, "Confirmada", "Confirmed")], ["#eab308", tr(lang, "Pendente QSL", "QSL pending")], [C.a5, tr(lang, "Faltando", "Needed")]];
    return (
      <Card style={{ padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
          <div style={{ fontWeight: 700, color: C.a3, fontSize: 13, fontFamily: "'Courier New',monospace" }}>{"\u{1F5FA}\uFE0F"} {tr(lang, "Mapa de conquistas", "Achievement map")}</div>
          <div style={{ display: "flex", gap: 12 }}>
            {legend.map(function(l, i) { return <span key={i} style={{ fontSize: 11, color: l[0] }}>{"\u25CF"} <span style={{ color: C.t3 }}>{l[1]}</span></span>; })}
          </div>
        </div>
        <AwardMap points={mapPoints} />
      </Card>
    );
  }

  function renderHuntNow() {
    return (
      <Card style={{ padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
          <div style={{ fontWeight: 700, color: C.a5, fontSize: 13, fontFamily: "'Courier New',monospace" }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: C.a5, marginRight: 6, boxShadow: "0 0 8px " + C.a5 }}></span>
            {tr(lang, "Caçar agora \u2014 ao vivo", "Hunt now \u2014 live")}
          </div>
          <span style={{ fontSize: 11, color: C.t3 }}>{tr(lang, "do DX cluster", "from DX cluster")}</span>
        </div>
        <div style={{ ...hint, marginBottom: 10 }}>
          {tab === "marathon"
            ? tr(lang, "Estações ativas agora que somam pontos ao seu Marathon de " + year + ".", "Stations active right now that score points for your " + year + " Marathon.")
            : tr(lang, "Estações ativas agora de entidades que você precisa trabalhar ou confirmar.", "Stations active right now from entities you need to work or confirm.")}
        </div>
        {spotsLoad ? (
          <div style={{ textAlign: "center", color: C.t3, fontSize: 12, padding: 16 }}>{tr(lang, "Carregando spots...", "Loading spots...")}</div>
        ) : spotsErr ? (
          <div style={{ textAlign: "center", color: C.t3, fontSize: 12, padding: 16 }}>{tr(lang, "Spots indisponíveis no momento.", "Spots unavailable right now.")}</div>
        ) : huntSpots.length === 0 ? (
          <div style={{ textAlign: "center", color: C.t3, fontSize: 12, padding: 16 }}>
            {tab === "marathon"
              ? tr(lang, "Nenhuma estação no ar agora soma pontos novos \u2014 continue de olho!", "No station on the air right now scores new points \u2014 keep watching!")
              : tr(lang, "Nenhuma entidade que você precisa está ativa agora.", "No entity you need is active right now.")}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 8 }}>
            {huntSpots.map(function(s, i) {
              var bc = BAND_COLORS[s.b] || C.t3;
              return (
                <div key={i} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: C.t1, fontFamily: "'Courier New',monospace" }}>{s.sc}</span>
                    <span style={{ fontSize: 11, color: bc, fontWeight: 700 }}>{s.b} {s.m}</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.t2 }}>{s.ent} <span style={{ color: C.t3 }}>({s.cont}{s.cq ? " \u00b7 CQ " + s.cq : ""})</span></div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
                    {s.tags.map(function(tg, ti) { return <span key={ti} style={{ background: tg.color + "20", border: "1px solid " + tg.color + "50", color: tg.color, fontSize: 10, fontWeight: 700, borderRadius: 5, padding: "2px 6px" }}>{tg.txt}</span>; })}
                    {s.f ? <span style={{ fontSize: 10, color: C.t3, fontFamily: "monospace", marginLeft: "auto" }}>{s.f} kHz</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    );
  }

  // ── Annual DX Marathon ranking panel ─────────────────────────
  function renderRanking() {
    var rows = (rank && rank.rows) || [];
    var myPos = -1;
    for (var i = 0; i < rows.length; i++) { if (rows[i].callsign === callsign) { myPos = i + 1; break; } }
    var medals = ["\u{1F947}", "\u{1F948}", "\u{1F949}"];
    return (
      <Card style={{ padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
          <div style={{ fontWeight: 700, color: C.a6, fontSize: 13, fontFamily: "'Courier New',monospace" }}>{"\u{1F3C6}"} {tr(lang, "Ranking DX Marathon ", "DX Marathon Ranking ") + year}</div>
          <span style={{ fontSize: 11, color: C.t3 }}>{rank ? rank.totalOperators + tr(lang, " operadores", " operators") : ""}</span>
        </div>
        <div style={{ ...hint, marginBottom: 10 }}>{tr(lang, "Comparativo entre todos os operadores HamExpert pelo placar do ano (entidades + zonas CQ).", "Across all HamExpert operators by this year's score (entities + CQ zones).")}</div>
        {rankLoad && rows.length === 0 ? (
          <div style={{ textAlign: "center", color: C.t3, fontSize: 12, padding: 16 }}>{tr(lang, "Carregando ranking...", "Loading ranking...")}</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: "center", color: C.t3, fontSize: 12, padding: 16 }}>{tr(lang, "Sem dados de ranking para este ano.", "No ranking data for this year.")}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "monospace" }}>
              <thead>
                <tr style={{ background: C.s3 }}>
                  <th style={{ padding: "6px 10px", textAlign: "center", color: C.t3, width: 50 }}>#</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", color: C.a1 }}>{tr(lang, "Operador", "Operator")}</th>
                  <th style={{ padding: "6px 8px", textAlign: "center", color: C.t3 }}>{tr(lang, "Ent", "Ent")}</th>
                  <th style={{ padding: "6px 8px", textAlign: "center", color: C.t3 }}>{tr(lang, "Zonas", "Zones")}</th>
                  <th style={{ padding: "6px 10px", textAlign: "right", color: C.a6 }}>{tr(lang, "Pontos", "Score")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(function(r, i) {
                  var me = r.callsign === callsign;
                  return (
                    <tr key={r.callsign + i} style={{ borderBottom: "1px solid " + C.bd + "15", background: me ? C.a1 + "1a" : i % 2 === 0 ? "transparent" : C.s3 + "20" }}>
                      <td style={{ padding: "5px 10px", textAlign: "center", color: C.t2, fontWeight: 700 }}>{i < 3 ? medals[i] : i + 1}</td>
                      <td style={{ padding: "5px 10px", color: me ? C.a1 : C.t1, fontWeight: me ? 800 : 600 }}>{r.callsign}{me ? " \u2190 " + tr(lang, "você", "you") : ""}</td>
                      <td style={{ padding: "5px 8px", textAlign: "center", color: C.a2 }}>{r.entities}</td>
                      <td style={{ padding: "5px 8px", textAlign: "center", color: C.a4 }}>{r.zones}</td>
                      <td style={{ padding: "5px 10px", textAlign: "right", color: C.a6, fontWeight: 800 }}>{r.score}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {myPos === -1 && callsign && rows.length >= 25 && (
              <div style={{ ...hint, textAlign: "center", marginTop: 8 }}>{tr(lang, "Você ainda não aparece no top 25 deste ano.", "You're not in this year's top 25 yet.")}</div>
            )}
          </div>
        )}
      </Card>
    );
  }

  // Hero ring: target is your best year (or a 50-pt baseline) so the ring fills toward your record
  var marathonTarget = Math.max(bestScore, yScore, 50);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.a1, fontFamily: "'Courier New',monospace" }}>{"\u{1F3C5}"} {tr(lang, "Premiações DX", "DX Awards")}</h2>
        <div style={{ fontSize: 12, color: C.t3, marginTop: 3 }}>{tr(lang, "Acompanhe seu DX Marathon anual e seu progresso rumo ao DXCC Honor Roll", "Track your annual DX Marathon and your progress toward the DXCC Honor Roll")}</div>
      </div>

      {/* ════════════════ HERO RINGS ════════════════ */}
      <Card glow={isNumberOne ? C.a6 : inHonorRoll ? C.a2 : C.a1} style={{ padding: "20px 22px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 0%, " + C.a1 + "18, transparent 70%)" }}></div>
        <div style={{ position: "relative", display: "flex", gap: 16, alignItems: "stretch", justifyContent: "center", flexWrap: "wrap" }}>
          <AwardTile
            icon={"\u{1F3C3}"} title={"Marathon " + year} color={C.a1} color2={C.a3}
            value={yScore} total={marathonTarget} centerSub={tr(lang, "pts", "pts")}
            sub={yEntities + " " + tr(lang, "entidades", "entities") + " + " + yZones + " " + tr(lang, "zonas CQ", "CQ zones")} />
          <AwardTile
            icon={"\u2B50"} title={tr(lang, "Confirmadas", "Confirmed")} color={C.a2} color2={"#22d3ee"}
            value={confirmedCount} total={TOTAL_ENTITIES} centerSub={"/ " + TOTAL_ENTITIES}
            sub={isNumberOne ? tr(lang, "\u{1F3C6} Honor Roll #1!", "\u{1F3C6} Honor Roll #1!") : inHonorRoll ? tr(lang, "no Honor Roll \u00b7 #1 em " + neededForTop, "in Honor Roll \u00b7 #1 in " + neededForTop) : tr(lang, "faltam " + neededForHR + " para o Honor Roll", neededForHR + " to reach Honor Roll")} />
          <AwardTile
            icon={"\u{1F30D}"} title={tr(lang, "Trabalhadas", "Worked")} color={C.a6} color2={C.a5}
            value={workedCount} total={TOTAL_ENTITIES} centerSub={"/ " + TOTAL_ENTITIES}
            sub={pendingConfirm.length + " " + tr(lang, "aguardando QSL", "awaiting QSL")} />
        </div>
      </Card>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {tabBtn("marathon", "DX Marathon", "\u{1F3C3}")}
        {tabBtn("honorroll", "DXCC Honor Roll", "\u{1F396}\uFE0F")}
        {tabBtn("challenge", "DXCC Challenge", "\u{1F30D}")}
        {tabBtn("more", tr(lang, "Conquistas", "More Awards"), "\u{1F31F}")}
        {tabBtn("efetividade", tr(lang, "Efetividade DX", "DX Effectiveness"), "\u{1F48E}")}
      </div>

      {/* ════════════════ DX MARATHON ════════════════ */}
      {tab === "marathon" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Year selector */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={function() { setYear(year - 1); }} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid " + C.bd, background: C.s2, color: C.a1, cursor: "pointer", fontSize: 16, fontWeight: 700 }}>{"\u2039"}</button>
              <div style={{ minWidth: 90, textAlign: "center", fontWeight: 800, fontSize: 20, color: C.t1, fontFamily: "'Courier New',monospace" }}>{year}</div>
              <button onClick={function() { if (year < nowYear) setYear(year + 1); }} disabled={year >= nowYear} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid " + C.bd, background: C.s2, color: year >= nowYear ? C.t3 : C.a1, cursor: year >= nowYear ? "default" : "pointer", opacity: year >= nowYear ? 0.4 : 1, fontSize: 16, fontWeight: 700 }}>{"\u203A"}</button>
            </div>
            <div style={{ fontSize: 11, color: C.t3, textAlign: "right", maxWidth: 420 }}>
              {tr(lang, "CQ DX Marathon: contagem anual (1 jan \u2013 31 dez UTC) de entidades DXCC + zonas CQ trabalhadas. Reinicia a cada ano.", "CQ DX Marathon: yearly count (Jan 1 \u2013 Dec 31 UTC) of DXCC entities + CQ zones worked. Resets every year.")}
            </div>
          </div>

          {/* Score cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
            <StatBox label={tr(lang, "Pontuação", "Score")} value={yScore} icon={"\u{1F3AF}"} color={C.a1} sub={tr(lang, "entidades + zonas", "entities + zones")} />
            <StatBox label={tr(lang, "Entidades", "Entities")} value={yEntities + " / " + TOTAL_ENTITIES} icon={"\u{1F30D}"} color={C.a6} />
            <StatBox label={tr(lang, "Zonas CQ", "CQ Zones")} value={yZones + " / 40"} icon={"\u{1F5FA}\uFE0F"} color={C.a4} />
            <StatBox label={tr(lang, "QSOs no ano", "QSOs in year")} value={yData.qsos} icon={"\u{1F4E1}"} color={C.a2} />
          </div>

          {/* Hunt now — live scoring opportunities */}
          {renderHuntNow()}

          {/* Progress */}
          <Card style={{ padding: 16 }}>
            <Bar label={tr(lang, "Entidades DXCC", "DXCC Entities")} value={yEntities} total={TOTAL_ENTITIES} color={C.a6} color2={C.a1} />
            <Bar label={tr(lang, "Zonas CQ", "CQ Zones")} value={yZones} total={40} color={C.a4} color2={C.a3} />
            {bestScore > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.t2 }}>
                {tr(lang, "Seu melhor ano: ", "Your best year: ")}<span style={{ color: C.a2, fontWeight: 700, fontFamily: "monospace" }}>{bestScore} {tr(lang, "pts", "pts")}</span>
                {year === nowYear && yScore < bestScore && <span style={{ color: C.t3 }}>{" \u2014 " + tr(lang, "faltam ", "need ") + (bestScore - yScore) + tr(lang, " pts p/ bater o recorde", " pts to beat it")}</span>}
              </div>
            )}
          </Card>

          {/* CQ Zones grid for the year */}
          <Card style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, color: C.a4, fontSize: 13, marginBottom: 10, fontFamily: "'Courier New',monospace" }}>{"\u{1F5FA}\uFE0F"} {tr(lang, "Zonas CQ em ", "CQ Zones in ")}{year} ({yZones}/40)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(10,1fr)", gap: 4 }}>
              {Array.from({ length: 40 }, function(_, i) {
                var z = i + 1;
                var has = yearDetail.zoneSet[z];
                var color = has ? C.a2 : C.t3 + "60";
                var bg = has ? C.a2 + "20" : C.s3 + "40";
                return (
                  <div key={z} style={{ background: bg, border: "1px solid " + color + "30", borderRadius: 6, padding: "5px 0", textAlign: "center", fontSize: 13, fontWeight: 700, color: color, fontFamily: "monospace" }}>{z}</div>
                );
              })}
            </div>
            {yZones < 40 && (
              <div style={{ marginTop: 8, fontSize: 11, color: C.t3 }}>
                {tr(lang, "Faltando: ", "Missing: ")}{Array.from({ length: 40 }, function(_, i) { return i + 1; }).filter(function(z) { return !yearDetail.zoneSet[z]; }).join(", ")}
              </div>
            )}
          </Card>

          {/* Achievement map (this year) */}
          {renderMap()}

          {/* Year-over-year history */}
          {marathon.history.length > 0 && (
            <Card style={{ padding: 16 }}>
              <div style={{ fontWeight: 700, color: C.a3, fontSize: 13, marginBottom: 10, fontFamily: "'Courier New',monospace" }}>{"\u{1F4C8}"} {tr(lang, "Evolução ano a ano", "Year-over-year evolution")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {marathon.history.map(function(h) {
                  var pct = bestScore > 0 ? (h.score / bestScore) * 100 : 0;
                  var sel = h.year === yStr;
                  return (
                    <div key={h.year} onClick={function() { setYear(parseInt(h.year, 10)); }} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "4px 6px", borderRadius: 6, background: sel ? C.a1 + "12" : "transparent" }}>
                      <span style={{ width: 44, fontFamily: "monospace", fontWeight: 700, color: sel ? C.a1 : C.t2, fontSize: 13 }}>{h.year}</span>
                      <div style={{ flex: 1, height: 18, background: C.s3, borderRadius: 4, overflow: "hidden", position: "relative" }}>
                        <div style={{ width: pct + "%", height: "100%", background: "linear-gradient(90deg," + C.a1 + "," + C.a3 + ")", borderRadius: 4 }}></div>
                      </div>
                      <span style={{ width: 130, textAlign: "right", fontSize: 11, color: C.t3, fontFamily: "monospace" }}>
                        <span style={{ color: C.a2, fontWeight: 700 }}>{h.score} pts</span> ({h.entities}+{h.zones})
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Annual ranking across all operators */}
          {renderRanking()}

          {/* Entities worked this year */}
          <Card>
            <div style={{ fontWeight: 700, color: C.a6, fontSize: 13, padding: "14px 16px 8px", fontFamily: "'Courier New',monospace" }}>{"\u{1F30D}"} {tr(lang, "Entidades trabalhadas em ", "Entities worked in ")}{year} ({yEntities})</div>
            {yearDetail.entList.length === 0 ? (
              <div style={{ padding: "10px 16px 18px", color: C.t3, fontSize: 12 }}>{tr(lang, "Nenhuma entidade registrada neste ano. Suba seu log ou opere para começar!", "No entities logged this year. Upload your log or get on the air to start!")}</div>
            ) : (
              <div style={{ overflowX: "auto", maxHeight: 360, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "monospace" }}>
                  <thead>
                    <tr style={{ background: C.s3, position: "sticky", top: 0 }}>
                      <th style={{ padding: "6px 12px", textAlign: "left", color: C.a1 }}>{tr(lang, "Entidade", "Entity")}</th>
                      <th style={{ padding: "6px 6px", textAlign: "center", color: C.t3 }}>Cont</th>
                      <th style={{ padding: "6px 6px", textAlign: "center", color: C.t3 }}>{tr(lang, "1º contato", "First")}</th>
                      <th style={{ padding: "6px 6px", textAlign: "center", color: C.t3 }}>{tr(lang, "Banda", "Band")}</th>
                      <th style={{ padding: "6px 12px", textAlign: "center", color: C.t3 }}>{tr(lang, "Modo", "Mode")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearDetail.entList.map(function(e, i) {
                      return (
                        <tr key={e.entity + "|" + i} style={{ borderBottom: "1px solid " + C.bd + "20", background: i % 2 === 0 ? "transparent" : C.s3 + "20" }}>
                          <td style={{ padding: "4px 12px", color: C.t1, fontWeight: 600 }}>{e.entity}</td>
                          <td style={{ padding: "4px 6px", textAlign: "center", color: C.t3 }}>{e.cont}</td>
                          <td style={{ padding: "4px 6px", textAlign: "center", color: C.t2 }}>{fmtDate(e.date)}</td>
                          <td style={{ padding: "4px 6px", textAlign: "center", color: C.a3 }}>{e.band}</td>
                          <td style={{ padding: "4px 12px", textAlign: "center", color: C.a4 }}>{e.mode}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ════════════════ DXCC HONOR ROLL ════════════════ */}
      {tab === "honorroll" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Status banner */}
          <Card glow={isNumberOne ? C.a6 : inHonorRoll ? C.a2 : C.a1} style={{ padding: "18px 20px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 15% 50%, " + (isNumberOne ? C.a6 : inHonorRoll ? C.a2 : C.a1) + "22, transparent 60%)" }}></div>
            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ fontSize: 46 }}>{isNumberOne ? "\u{1F451}" : inHonorRoll ? "\u{1F396}\uFE0F" : "\u{1F3AF}"}</div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 11, letterSpacing: 1.5, color: C.t3, fontWeight: 800, textTransform: "uppercase", fontFamily: "'Courier New',monospace" }}>{tr(lang, "Status DXCC Honor Roll", "DXCC Honor Roll Status")}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: isNumberOne ? C.a6 : inHonorRoll ? C.a2 : C.t1, fontFamily: "'Courier New',monospace", lineHeight: 1.2 }}>
                  {isNumberOne ? tr(lang, "Honor Roll #1 \u2014 Topo do mundo!", "Honor Roll #1 \u2014 Top of the world!")
                    : inHonorRoll ? tr(lang, "Você está no Honor Roll!", "You're on the Honor Roll!")
                      : tr(lang, "Rumo ao Honor Roll", "On the road to Honor Roll")}
                </div>
                <div style={{ fontSize: 13, color: C.t2, marginTop: 4 }}>
                  {isNumberOne ? tr(lang, "Todas as entidades atuais confirmadas. Parabéns!", "All current entities confirmed. Congratulations!")
                    : inHonorRoll ? tr(lang, "Faltam ", "Need ") + neededForTop + tr(lang, " confirmadas para o cobiçado #1.", " more confirmed for the coveted #1.")
                      : tr(lang, "Faltam ", "Need ") + neededForHR + tr(lang, " confirmadas para entrar no Honor Roll.", " more confirmed to reach the Honor Roll.")}
                </div>
              </div>
            </div>
          </Card>

          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
            <StatBox label={tr(lang, "Confirmadas", "Confirmed")} value={confirmedCount} icon={"\u2B50"} color={C.a2} sub={"de " + TOTAL_ENTITIES} />
            <StatBox label={tr(lang, "Trabalhadas", "Worked")} value={workedCount} icon={"\u{1F4CB}"} color={C.a6} sub={"de " + TOTAL_ENTITIES} />
            <StatBox label={tr(lang, "Honor Roll (faltam)", "Honor Roll (need)")} value={neededForHR} icon={"\u{1F396}\uFE0F"} color={C.a1} sub={"\u2265 " + HR_THRESHOLD + " conf."} />
            <StatBox label={tr(lang, "#1 (faltam)", "#1 (need)")} value={neededForTop} icon={"\u{1F451}"} color={C.a4} sub={"= " + TOTAL_ENTITIES + " conf."} />
          </div>

          {/* Progress bars */}
          <Card style={{ padding: 16 }}>
            <Bar label={tr(lang, "Progresso ao Honor Roll", "Progress to Honor Roll")} value={confirmedCount} total={HR_THRESHOLD} color={C.a1} color2={C.a2} />
            <Bar label={tr(lang, "Progresso ao #1", "Progress to #1")} value={confirmedCount} total={TOTAL_ENTITIES} color={C.a4} color2={C.a6} />
            <div style={{ marginTop: 10, fontSize: 11, color: C.t3, lineHeight: 1.6 }}>
              {tr(lang,
                "O DXCC Honor Roll é concedido a quem confirma pelo menos (total \u2212 9) entidades atuais. O #1 Honor Roll exige todas confirmadas. Pela regra do ARRL DXCC, só contam confirmações via LoTW (mesmo número do LoTW).",
                "The DXCC Honor Roll is awarded for confirming at least (total \u2212 9) current entities. #1 Honor Roll requires all confirmed. Per ARRL DXCC rules, only LoTW confirmations count (same number as LoTW).")}
            </div>
          </Card>

          {/* Hunt now — live spots of needed/unconfirmed entities */}
          {renderHuntNow()}

          {/* Cumulative DXCC growth curve + Honor Roll projection */}
          {cumulative.series.length > 1 && (
            <Card style={{ padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 6 }}>
                <div style={{ fontWeight: 700, color: C.a3, fontSize: 13, fontFamily: "'Courier New',monospace" }}>{"\u{1F4C8}"} {tr(lang, "Curva de crescimento DXCC", "DXCC growth curve")}</div>
                {cumulative.proj && cumulative.proj.remainingToHR > 0 && (
                  <span style={{ fontSize: 11, color: C.t2, fontFamily: "monospace" }}>
                    {"\u2248 " + Math.round(cumulative.proj.ratePerYear) + tr(lang, " ent/ano", " ent/yr")}
                    {" \u00b7 "}
                    {tr(lang, "Honor Roll ~", "Honor Roll ~") + cumulative.proj.etaYear}
                  </span>
                )}
              </div>
              <div style={{ ...hint, marginBottom: 8 }}>
                {cumulative.proj && cumulative.proj.remainingToHR > 0
                  ? tr(lang, "No seu ritmo atual, faltam ~", "At your current pace, ~") + cumulative.proj.remainingToHR + tr(lang, " entidades para o Honor Roll, estimado para ", " entities remain for the Honor Roll, estimated around ") + cumulative.proj.etaMonth + "/" + cumulative.proj.etaYear + "."
                  : tr(lang, "Entidades únicas trabalhadas ao longo do tempo (acumulado).", "Unique entities worked over time (cumulative).")}
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={cumulative.series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dxccGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.a1} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={C.a1} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.bd} opacity={0.3} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.t3 }} minTickGap={28} />
                  <YAxis tick={{ fontSize: 10, fill: C.t3 }} width={34} />
                  <Tooltip contentStyle={tooltipStyle} formatter={function(v) { return [v, tr(lang, "Entidades", "Entities")]; }} />
                  <ReferenceLine y={HR_THRESHOLD} stroke={C.a2} strokeDasharray="5 4" label={{ value: "Honor Roll", fill: C.a2, fontSize: 10, position: "insideTopRight" }} />
                  <Area type="monotone" dataKey="dxcc" stroke={C.a1} strokeWidth={2} fill="url(#dxccGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Continent breakdown */}
          <Card style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, color: C.a3, fontSize: 13, marginBottom: 10, fontFamily: "'Courier New',monospace" }}>{"\u{1F310}"} {tr(lang, "Confirmadas por continente", "Confirmed by continent")}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {CONT_LIST.map(function(c) {
                var cs = contSummary[c] || { total: 0, worked: 0, confirmed: 0 };
                var pct = cs.total > 0 ? (cs.confirmed / cs.total) * 100 : 0;
                return (
                  <div key={c} style={{ flex: "1 1 130px", background: C.s2, border: "1px solid " + C.bd, borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{CONT_NAMES[c] || c}</span>
                      <span style={{ fontSize: 11, color: C.a2, fontFamily: "monospace" }}>{cs.confirmed}/{cs.total}</span>
                    </div>
                    <div style={{ height: 6, background: C.s3, borderRadius: 4, overflow: "hidden", marginTop: 6 }}>
                      <div style={{ width: pct + "%", height: "100%", background: C.a2, borderRadius: 4 }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Achievement map */}
          {renderMap()}

          {/* QSL pending — actionable */}
          {pendingConfirm.length > 0 && (
            <Card style={{ padding: 16 }}>
              <div style={{ fontWeight: 700, color: C.a6, fontSize: 13, marginBottom: 6, fontFamily: "'Courier New',monospace" }}>{"\u{1F4EC}"} {tr(lang, "Confirmação pendente (QSL)", "Pending confirmation (QSL)")} \u2014 {pendingConfirm.length}</div>
              <div style={{ ...hint, marginBottom: 8 }}>{tr(lang, "Entidades já trabalhadas mas ainda não confirmadas. Solicite o QSL/LoTW para somar ao seu Honor Roll.", "Entities already worked but not yet confirmed. Request the QSL/LoTW to count toward your Honor Roll.")}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {pendingConfirm.map(function(r) {
                  return <span key={r.px} style={{ background: "#eab308" + "18", border: "1px solid #eab30840", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#eab308", fontFamily: "monospace" }}>{r.entity}</span>;
                })}
              </div>
            </Card>
          )}

          {/* Searchable world entity table */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 700, color: C.a1, fontSize: 13, fontFamily: "'Courier New',monospace" }}>{"\u{1F50D}"} {tr(lang, "Entidades do mundo", "World entities")}</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input placeholder={tr(lang, "Buscar prefixo/país...", "Search prefix/country...")} value={hrSearch} onChange={function(e) { setHrSearch(e.target.value); }} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 6, padding: "5px 10px", color: C.t1, fontSize: 12, width: 180, outline: "none" }} />
              <select value={hrCont} onChange={function(e) { setHrCont(e.target.value); }} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 6, padding: "5px 8px", color: C.t1, fontSize: 12, outline: "none" }}>
                <option value="">{tr(lang, "Todos cont.", "All cont.")}</option>
                {CONT_LIST.map(function(c) { return <option key={c} value={c}>{CONT_NAMES[c]}</option>; })}
              </select>
              <select value={hrStatus} onChange={function(e) { setHrStatus(e.target.value); }} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 6, padding: "5px 8px", color: C.t1, fontSize: 12, outline: "none" }}>
                <option value="">{tr(lang, "Todos status", "All status")}</option>
                <option value="confirmed">{tr(lang, "Confirmadas", "Confirmed")}</option>
                <option value="worked">{tr(lang, "Pendentes QSL", "QSL pending")}</option>
                <option value="needed">{tr(lang, "Faltando", "Needed")}</option>
              </select>
              <span style={{ color: C.t3, fontSize: 12 }}>{filtered.length}</span>
            </div>
          </div>
          <Card>
            <div style={{ overflowX: "auto", maxHeight: 560, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "monospace" }}>
                <thead>
                  <tr style={{ background: C.s3, position: "sticky", top: 0, zIndex: 2 }}>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: C.a1 }}>{tr(lang, "Prefixo", "Prefix")}</th>
                    <th style={{ padding: "6px 10px", textAlign: "left", color: C.a1 }}>{tr(lang, "Entidade", "Entity")}</th>
                    <th style={{ padding: "6px 6px", textAlign: "center", color: C.t3 }}>Cont</th>
                    <th style={{ padding: "6px 6px", textAlign: "center", color: C.t3 }}>CQ</th>
                    <th style={{ padding: "6px 10px", textAlign: "center", color: C.t3 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(function(r, i) {
                    var clr = r.confirmed ? C.a2 : r.worked ? "#eab308" : C.t3;
                    var label = r.confirmed ? "\u2B50 " + tr(lang, "Confirmada", "Confirmed") : r.worked ? "\u25CF " + tr(lang, "Pendente QSL", "QSL pending") : "\u2014 " + tr(lang, "Faltando", "Needed");
                    return (
                      <tr key={r.px} style={{ borderBottom: "1px solid " + C.bd + "15", background: i % 2 === 0 ? "transparent" : C.s3 + "20" }}>
                        <td style={{ padding: "4px 10px", color: C.a3, fontWeight: 600 }}>{r.px}</td>
                        <td style={{ padding: "4px 10px", color: r.worked ? C.t1 : C.t3, fontWeight: r.worked ? 600 : 400 }}>{r.entity}</td>
                        <td style={{ padding: "4px 6px", textAlign: "center", color: C.t3 }}>{r.cont}</td>
                        <td style={{ padding: "4px 6px", textAlign: "center", color: C.t3 }}>{r.cq}</td>
                        <td style={{ padding: "4px 10px", textAlign: "center", color: clr, whiteSpace: "nowrap" }}>{label}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 16, padding: "10px 8px 8px", justifyContent: "center" }}>
              <span style={{ fontSize: 11, color: C.a2 }}>{"\u2B50"} {tr(lang, "Confirmada", "Confirmed")}</span>
              <span style={{ fontSize: 11, color: "#eab308" }}>{"\u25CF"} {tr(lang, "Pendente QSL", "QSL pending")}</span>
              <span style={{ fontSize: 11, color: C.t3 }}>{"\u2014"} {tr(lang, "Faltando", "Needed")}</span>
            </div>
          </Card>
        </div>
      )}

      {/* ════════════════ DXCC CHALLENGE (band/mode matrix) ════════════════ */}
      {tab === "challenge" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.6 }}>
            {tr(lang,
              "DXCC Challenge: conta cada entidade em cada banda (slots banda-entidade). Veja também WAC (todos os continentes) e WAZ (40 zonas CQ), com a matriz completa por banda/modo.",
              "DXCC Challenge: counts each entity on each band (band-entity slots). Also shows WAC (all continents) and WAZ (40 CQ zones), with the full band/mode matrix.")}
          </div>

          {/* Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            <StatBox label={t(lang, "dxccWorked")} value={workedCount} icon={"\u{1F4CB}"} color={C.a6} />
            <StatBox label={t(lang, "dxccConfirmed") + " (LoTW)"} value={confirmedCount} icon={"\u2B50"} color={C.a2} />
            <StatBox label={t(lang, "dxccNeeded")} value={TOTAL_ENTITIES - workedCount} icon={"\u{1F3AF}"} color={C.a5} />
          </div>

          {/* Continent breakdown */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CONT_LIST.map(function(c) {
              var cs = contSummary[c] || { total: 0, worked: 0, confirmed: 0 };
              return (
                <div key={c} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 8, padding: "6px 12px", textAlign: "center", minWidth: 100 }}>
                  <div style={{ fontSize: 11, color: C.t3 }}>{CONT_NAMES[c] || c}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.a1, fontFamily: "monospace" }}>{cs.worked}/{cs.total}</div>
                  <div style={{ fontSize: 10, color: C.a2 }}>{cs.confirmed} {t(lang, "dxccConfirmed")}</div>
                </div>
              );
            })}
          </div>

          {/* WAC */}
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: 700, color: C.a6, fontSize: 13, marginBottom: 10, fontFamily: "'Courier New',monospace" }}>{"\u{1F310}"} {t(lang, "wacTitle")} ({(stats && stats.wacWorked) || 0}/6)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["AF", "AS", "EU", "NA", "OC", "SA"].map(function(c) {
                var wd = stats && stats.wacData ? stats.wacData[c] : null;
                var color = wd && wd.confirmed ? C.a2 : wd && wd.worked ? "#eab308" : C.t3;
                var bg = wd && wd.confirmed ? C.a2 + "20" : wd && wd.worked ? "#eab308" + "20" : C.s3;
                return (
                  <div key={c} style={{ background: bg, border: "1px solid " + color + "40", borderRadius: 8, padding: "8px 16px", textAlign: "center", minWidth: 80 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: color }}>{c}</div>
                    <div style={{ fontSize: 10, color: C.t3 }}>{CONT_NAMES[c]}</div>
                    <div style={{ fontSize: 10, marginTop: 2, color: color }}>{wd ? (wd.confirmed ? "\u2B50 " + t(lang, "dxccConfirmed") : "\u2714 " + t(lang, "dxccWorked")) : "\u2014"}</div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* WAZ */}
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: 700, color: C.a4, fontSize: 13, marginBottom: 10, fontFamily: "'Courier New',monospace" }}>{"\u{1F5FA}\uFE0F"} {t(lang, "wazTitle")} ({(stats && stats.wazWorked) || 0}/40)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(10,1fr)", gap: 4 }}>
              {Array.from({ length: 40 }, function(_, i) {
                var z = i + 1;
                var zd = stats && stats.wazData ? stats.wazData[z] : null;
                var color = zd && zd.confirmed ? C.a2 : zd && zd.worked ? "#eab308" : C.t3 + "60";
                var bg = zd && zd.confirmed ? C.a2 + "20" : zd && zd.worked ? "#eab308" + "20" : C.s3 + "40";
                return (
                  <div key={z} style={{ background: bg, border: "1px solid " + color + "30", borderRadius: 6, padding: "4px 0", textAlign: "center" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: color, fontFamily: "monospace" }}>{z}</div>
                    {zd && <div style={{ fontSize: 9, color: C.t3 }}>{zd.count}</div>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 8, justifyContent: "center" }}>
              <span style={{ fontSize: 11, color: C.a2 }}>{"\u25CF"} {t(lang, "dxccConfirmed")} ({(stats && stats.wazConfirmed) || 0})</span>
              <span style={{ fontSize: 11, color: "#eab308" }}>{"\u25CF"} {t(lang, "dxccWorked")} ({(stats && stats.wazWorked) || 0})</span>
              <span style={{ fontSize: 11, color: C.t3 }}>{"\u25CF"} {t(lang, "dxccNeeded")} ({40 - ((stats && stats.wazWorked) || 0)})</span>
            </div>
          </Card>

          {/* Tabs + Filters */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {matrixBtn("band", t(lang, "dxccByBand"))}
              {matrixBtn("mode", t(lang, "dxccByMode"))}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input placeholder={tr(lang, "Buscar...", "Search...")} value={chSearch} onChange={function(e) { setChSearch(e.target.value); }} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 6, padding: "4px 10px", color: C.t1, fontSize: 12, width: 160, outline: "none" }} />
              <select value={chCont} onChange={function(e) { setChCont(e.target.value); }} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 6, padding: "4px 8px", color: C.t1, fontSize: 12, outline: "none" }}>
                <option value="">{t(lang, "dxccAll")}</option>
                {CONT_LIST.map(function(c) { return <option key={c} value={c}>{CONT_NAMES[c]}</option>; })}
              </select>
              <select value={chStatus} onChange={function(e) { setChStatus(e.target.value); }} style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 6, padding: "4px 8px", color: C.t1, fontSize: 12, outline: "none" }}>
                <option value="">{t(lang, "dxccAll")}</option>
                <option value="worked">{t(lang, "dxccWorked")}</option>
                <option value="confirmed">{t(lang, "dxccConfirmed")}</option>
                <option value="needed">{t(lang, "dxccNeeded")}</option>
              </select>
              <span style={{ color: C.t3, fontSize: 12 }}>{chFiltered.length} {t(lang, "dxccEntity")}</span>
            </div>
          </div>

          {/* Matrix Table */}
          <Card>
            <div style={{ overflowX: "auto", maxHeight: 600, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: "monospace" }}>
                <thead>
                  <tr style={{ background: C.s3, position: "sticky", top: 0, zIndex: 2 }}>
                    <th style={{ padding: "6px 8px", textAlign: "left", color: C.a1, borderBottom: "1px solid " + C.bd, position: "sticky", left: 0, background: C.s3, zIndex: 3, minWidth: 160 }}>{t(lang, "dxccEntity")}</th>
                    <th style={{ padding: "6px 4px", textAlign: "center", color: C.t3, borderBottom: "1px solid " + C.bd, minWidth: 30 }}>Cont</th>
                    {(matrixTab === "band" ? BAND_ORDER : activeModes).map(function(col) {
                      return <th key={col} style={{ padding: "6px 2px", textAlign: "center", color: C.a1, borderBottom: "1px solid " + C.bd, minWidth: 42 }}>{col}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {chFiltered.map(function(r, ri) {
                    var cols = matrixTab === "band" ? BAND_ORDER : activeModes;
                    var dataMap = matrixTab === "band" ? r.bands : r.modes;
                    return (
                      <tr key={r.px} style={{ borderBottom: "1px solid " + C.bd + "10", background: ri % 2 === 0 ? "transparent" : C.s3 + "20" }}>
                        <td style={{ padding: "4px 8px", color: r.confirmed ? C.a2 : r.worked ? "#eab308" : C.t3, fontWeight: r.worked ? 600 : 400, position: "sticky", left: 0, background: ri % 2 === 0 ? C.bg : C.s1, zIndex: 1, whiteSpace: "nowrap" }}>{r.confirmed && "\u2B50"}{r.worked && !r.confirmed ? "\u25CF " : ""}{!r.worked ? "  " : ""}{r.entity}</td>
                        <td style={{ padding: "4px 4px", textAlign: "center", color: C.t3, fontSize: 10 }}>{r.cont}</td>
                        {cols.map(function(col) {
                          var cell = dataMap[col];
                          var bg2 = cell && cell.c ? C.a2 + "30" : cell && cell.w ? "#eab308" + "25" : "transparent";
                          var sym = cell && cell.c ? "\u2B50" : cell && cell.w ? "\u25CF" : "";
                          var clr = cell && cell.c ? C.a2 : cell && cell.w ? "#eab308" : "transparent";
                          return <td key={col} style={{ padding: "3px 2px", textAlign: "center", background: bg2, color: clr, fontSize: 11 }}>{sym}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 16, padding: "10px 8px 4px", justifyContent: "center" }}>
              <span style={{ fontSize: 11, color: C.a2 }}>{"\u2B50"} {t(lang, "dxccConfirmed")} LoTW</span>
              <span style={{ fontSize: 11, color: "#eab308" }}>{"\u25CF"} {t(lang, "dxccWorked")}</span>
              <span style={{ fontSize: 11, color: C.t3 }}>{"\u2014"} {t(lang, "dxccNeeded")}</span>
            </div>
          </Card>
        </div>
      )}

      {/* ════════════════ MORE AWARDS (5BDXCC · WPX) ════════════════ */}
      {tab === "efetividade" && (
        <DXEffectiveness lang={lang} qsos={qsos} dd={dd} />
      )}

      {tab === "more" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.6 }}>
            {tr(lang,
              "Prêmios adicionais reconhecidos mundialmente, calculados a partir do seu log. As confirmações usam QSL via LoTW ou QRZ.",
              "Additional world-recognized awards computed from your log. Confirmations use LoTW or QRZ QSLs.")}
          </div>

          {/* ── 5BDXCC ── */}
          <Card style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 6 }}>
              <div style={{ fontWeight: 700, color: C.a4, fontSize: 14, fontFamily: "'Courier New',monospace" }}>{"\u{1F4FB}"} 5-Band DXCC (5BDXCC)</div>
              <span style={{ fontSize: 13, fontWeight: 800, color: fiveBand.bandsComplete === 5 ? C.a2 : C.a4, fontFamily: "monospace" }}>{fiveBand.bandsComplete}/5 {tr(lang, "bandas", "bands")}</span>
            </div>
            <div style={{ ...hint, marginBottom: 12 }}>
              {tr(lang, "Exige 100 entidades DXCC confirmadas em cada uma das 5 bandas clássicas (80, 40, 20, 15 e 10 m).", "Requires 100 confirmed DXCC entities on each of the 5 classic bands (80, 40, 20, 15 and 10 m).")}
            </div>
            {FIVE_BANDS.map(function(b) {
              var pb = fiveBand.perBand[b];
              var done = pb.confirmed >= 100;
              return (
                <div key={b} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                    <span style={{ color: BAND_COLORS[b] || C.t2, fontWeight: 700, fontFamily: "monospace" }}>{done ? "\u2705 " : ""}{b}</span>
                    <span style={{ color: C.t3, fontFamily: "monospace" }}>
                      <span style={{ color: C.a2, fontWeight: 700 }}>{pb.confirmed}</span> conf. / {pb.worked} {tr(lang, "trab.", "wkd")} <span style={{ color: done ? C.a2 : C.t3 }}>({Math.min(100, pb.confirmed)}/100)</span>
                    </span>
                  </div>
                  <div style={{ height: 12, background: C.s3, borderRadius: 6, overflow: "hidden", position: "relative" }}>
                    <div style={{ width: Math.min(100, pb.worked) + "%", height: "100%", background: (BAND_COLORS[b] || C.t3) + "33", position: "absolute", top: 0, left: 0 }}></div>
                    <div style={{ width: Math.min(100, pb.confirmed) + "%", height: "100%", background: "linear-gradient(90deg," + (BAND_COLORS[b] || C.a4) + "," + C.a2 + ")", borderRadius: 6, position: "absolute", top: 0, left: 0, transition: "width .5s" }}></div>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 8, fontSize: 11, color: fiveBand.bandsComplete === 5 ? C.a2 : C.t3, fontWeight: fiveBand.bandsComplete === 5 ? 700 : 400 }}>
              {fiveBand.bandsComplete === 5
                ? tr(lang, "\u{1F3C6} Parabéns! Você completou o 5BDXCC.", "\u{1F3C6} Congratulations! You completed 5BDXCC.")
                : tr(lang, "Barra clara = trabalhadas \u00b7 barra cheia = confirmadas (LoTW).", "Light bar = worked \u00b7 filled bar = confirmed (LoTW).")}
            </div>
          </Card>

          {/* ── CQ WPX ── */}
          <Card style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, color: C.a3, fontSize: 14, marginBottom: 4, fontFamily: "'Courier New',monospace" }}>{"\u{1F310}"} CQ WPX {tr(lang, "(prefixos)", "(prefixes)")}</div>
            <div style={{ ...hint, marginBottom: 12 }}>
              {tr(lang, "Conta prefixos únicos de indicativos trabalhados (ex.: PY2, K1, DL9). O nível básico Mixed começa em 300 prefixos.", "Counts unique callsign prefixes worked (e.g. PY2, K1, DL9). The basic Mixed level starts at 300 prefixes.")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, marginBottom: 12 }}>
              <StatBox label={tr(lang, "Prefixos trabalhados", "Prefixes worked")} value={wpx.worked} icon={"\u{1F4CB}"} color={C.a6} />
              <StatBox label={tr(lang, "Prefixos confirmados", "Prefixes confirmed")} value={wpx.confirmed} icon={"\u2B50"} color={C.a2} />
            </div>
            <Bar label={tr(lang, "Rumo ao WPX 300 (Mixed)", "Toward WPX 300 (Mixed)")} value={Math.min(300, wpx.confirmed)} total={300} color={C.a3} color2={C.a2} />
            {wpx.confirmed >= 300 && <Bar label={tr(lang, "Rumo ao WPX 400", "Toward WPX 400")} value={Math.min(400, wpx.confirmed)} total={400} color={C.a4} color2={C.a6} />}
            {wpx.confirmed >= 400 && <Bar label={tr(lang, "Rumo ao WPX 500 (Honor Roll WPX)", "Toward WPX 500 (WPX Honor Roll)")} value={Math.min(500, wpx.confirmed)} total={500} color={C.a1} color2={C.a6} />}
          </Card>

          {/* ── WAS note (no per-QSO state data) ── */}
          <Card style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, color: C.t2, fontSize: 14, marginBottom: 4, fontFamily: "'Courier New',monospace" }}>{"\u{1F1FA}\u{1F1F8}"} WAS {tr(lang, "(Worked All States)", "(Worked All States)")}</div>
            <div style={hint}>
              {tr(lang, "O WAS exige o estado (US state) de cada contato, informação que ainda não é importada do seu log. Em breve.", "WAS requires the US state of each contact, which isn't yet imported from your log. Coming soon.")}
            </div>
          </Card>

        </div>
      )}
    </div>
  );
}
